export interface CompanyOpportunityGroup {
    key: string;
    label: string;
    items: string[];
    icon: string;
}

export interface CompanyTextGroup {
    key: string;
    label: string;
    items: string[];
}

export type CompanySummaryRecord = Record<string, any>;

const UNAVAILABLE_VALUE_PATTERN = /^(?:n\/?a|not available|no details available\.?|unknown)$/i;

export const isUsableText = (value: unknown): value is string => (
    typeof value === 'string' && value.trim().length > 0 && !UNAVAILABLE_VALUE_PATTERN.test(value.trim())
);

export const humanizeKey = (value: string) => value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const parseCompanySummary = (value: unknown): CompanySummaryRecord | null => {
    if (!value) return null;
    if (typeof value === 'object' && !Array.isArray(value)) return value as CompanySummaryRecord;
    if (typeof value !== 'string') return null;

    try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? parsed as CompanySummaryRecord
            : null;
    } catch {
        return null;
    }
};

export const mergeCompanySummaries = (rawValue: unknown, primaryValue: unknown): CompanySummaryRecord | null => {
    const raw = parseCompanySummary(rawValue);
    const primary = parseCompanySummary(primaryValue);
    if (!raw && !primary) return null;

    const merged = { ...(raw || {}), ...(primary || {}) };
    for (const key of ['business_profile', 'market_position', 'pain_points', 'opportunities', 'engagement_strategy']) {
        const rawSection = raw?.[key];
        const primarySection = primary?.[key];
        if (
            (rawSection && typeof rawSection === 'object' && !Array.isArray(rawSection))
            || (primarySection && typeof primarySection === 'object' && !Array.isArray(primarySection))
        ) {
            merged[key] = {
                ...(rawSection && typeof rawSection === 'object' && !Array.isArray(rawSection) ? rawSection : {}),
                ...(primarySection && typeof primarySection === 'object' && !Array.isArray(primarySection) ? primarySection : {}),
            };
        }
    }

    return merged;
};

export const toDisplayItems = (value: unknown, nestedLabel?: string): string[] => {
    if (value === null || value === undefined || value === false) return [];
    if (typeof value === 'string') {
        if (!isUsableText(value)) return [];
        return [nestedLabel ? `${nestedLabel}: ${value.trim()}` : value.trim()];
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        const displayValue = String(value);
        return [nestedLabel ? `${nestedLabel}: ${displayValue}` : displayValue];
    }
    if (Array.isArray(value)) return value.flatMap((item) => toDisplayItems(item, nestedLabel));
    if (typeof value === 'object') {
        return Object.entries(value as Record<string, unknown>).flatMap(([key, nestedValue]) =>
            toDisplayItems(nestedValue, humanizeKey(key))
        );
    }
    return [];
};

export const uniqueItems = (items: string[]) => Array.from(new Set(items));

export const buildCompanyTextGroups = (value: unknown): CompanyTextGroup[] => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    return Object.entries(value as Record<string, unknown>).flatMap(([key, fieldValue]) => {
        const items = uniqueItems(toDisplayItems(fieldValue));
        return items.length > 0 ? [{ key, label: humanizeKey(key), items }] : [];
    });
};

export const buildCompanyOpportunityGroups = (opportunities: unknown): CompanyOpportunityGroup[] => {
    if (!opportunities || typeof opportunities !== 'object' || Array.isArray(opportunities)) return [];
    const source = opportunities as Record<string, unknown>;
    const consumedKeys = new Set<string>();
    const groups: CompanyOpportunityGroup[] = [];

    const addCanonicalGroup = (
        key: string,
        label: string,
        aliases: string[],
        icon: string,
    ) => {
        aliases.forEach((alias) => consumedKeys.add(alias));
        const items = uniqueItems(aliases.flatMap((alias) => toDisplayItems(source[alias])));
        if (items.length > 0) groups.push({ key, label, items, icon });
    };

    addCanonicalGroup('growth-vectors', 'Growth Vectors', ['growth_vectors', 'growth_opportunities'], 'bx:trending-up');
    addCanonicalGroup('market-expansion', 'Market Expansion', ['market_expansion'], 'bx:world');
    addCanonicalGroup('strategic-partnerships', 'Strategic Partnerships', ['strategic_partnerships'], 'bx:group');
    addCanonicalGroup('strategic-priorities', 'Strategic Priorities', ['strategic_priorities'], 'bx:target-lock');
    addCanonicalGroup('innovation-areas', 'Innovation Areas', ['innovation_areas'], 'bx:bulb');
    addCanonicalGroup('digital-transformation', 'Digital Transformation', ['digital_transformation'], 'bx:chip');
    addCanonicalGroup('partnership-potential', 'Partnership Potential', ['partnership_potential'], 'bx:link-alt');

    Object.entries(source).forEach(([key, value]) => {
        if (consumedKeys.has(key)) return;
        const items = uniqueItems(toDisplayItems(value));
        if (items.length === 0) return;
        groups.push({
            key,
            label: humanizeKey(key),
            items,
            icon: 'bx:bulb',
        });
    });

    return groups;
};

export const formatMoneyValue = (value: unknown) => {
    const currency = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    });
    if (typeof value === 'number' && Number.isFinite(value)) return currency.format(value);
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (!value || typeof value !== 'object' || Array.isArray(value)) return '';

    const range = value as Record<string, unknown>;
    const minimum = typeof range.min === 'number' ? currency.format(range.min) : '';
    const maximum = typeof range.max === 'number' ? currency.format(range.max) : '';
    if (minimum && maximum) return `${minimum}–${maximum}`;
    return minimum || maximum;
};

export const formatTimeline = (value: unknown) => {
    if (!isUsableText(value)) return '';
    const normalized = value.trim().replace(/\s*-\s*/g, '–');
    return /month/i.test(normalized) ? normalized : `${normalized} months`;
};

export const getOpportunityTone = (urgency: unknown) => {
    const normalized = typeof urgency === 'string' ? urgency.toLowerCase() : '';
    if (normalized === 'high' || normalized === 'critical') return 'border-rose-400/25 bg-rose-400/10 text-rose-200';
    if (normalized === 'medium') return 'border-amber-300/25 bg-amber-300/10 text-amber-100';
    return 'border-white/10 bg-white/[0.04] text-gray-300';
};

export const getOpportunityRecords = (value: unknown): Array<Record<string, any>> => {
    let parsedValue = value;
    if (typeof value === 'string') {
        try {
            parsedValue = JSON.parse(value);
        } catch {
            return [];
        }
    }
    if (!Array.isArray(parsedValue)) return [];
    return parsedValue.filter((item): item is Record<string, any> => (
        Boolean(item) && typeof item === 'object' && !Array.isArray(item)
    ));
};
